import { Button, Image, Modal, ModalProps } from 'antd';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { v4 as uuidv4 } from 'uuid';
import { getVideoMetadata } from 'utils/validator/video';
import { VideoPreviewer } from 'components/Dialogue/components/Message/VideoMessage/VideoPreview';
import { useTranslate } from '@tolgee/react';
import { useMediaPreview, useSetMediaPreview } from './useMediaPreview';
import { useSendFileImage } from '../MessageEditor/Toolbar/ImageUpload';
import { useSendFileVideo } from '../MessageEditor/Toolbar/VideoUpload/hooks/useUploadVideo';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';

const Content = styled.div`
  max-height: 60vh;
  margin: -24px;
  padding: 24px;
  overflow: auto;
`;

const VideoWrapper = styled.div`
  display: inline-block;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  > * {
    margin-left: 8px;
  }
`;

function MediaPreview() {
  const { visible, type, files } = useMediaPreview();

  const setMediaPreview = useSetMediaPreview();

  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const { t } = useTranslate();

  useEffect(() => {
    return () => {
      fileUrls.forEach(URL.revokeObjectURL);
    };
  }, [fileUrls]);

  useEffect(() => {
    setFileUrls(files.map(URL.createObjectURL));
  }, [files]);

  const sendFileImage = useSendFileImage();

  const sendFileVideo = useSendFileVideo();

  const onCancel = () => {
    setMediaPreview((pre) => {
      return {
        ...pre,
        visible: false,
      };
    });
  };

  const sendImage = () => {
    files.forEach((file) => {
      sendFileImage(file);
    });

    onCancel();
  };

  const sendVideo = () => {
    files.forEach((file) => {
      const url = URL.createObjectURL(file);

      getVideoMetadata(url)
        .then(({ width, height }) => {
          const uuid = uuidv4();
          sendFileVideo(file, width, height, uuid);
        })
        .finally(() => {
          URL.revokeObjectURL(url);
        });
    });

    onCancel();
  };

  useKeyboardHandler(
    ['Enter'],
    () => {
      if (type === 'image') {
        sendImage();
      }

      if (type === 'video') {
        sendVideo();
      }
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.MODAL,
      context: () => !!type && files.length > 0 && visible,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  if (!type) {
    return null;
  }

  const afterClose = () => {
    setMediaPreview((pre) => {
      return {
        ...pre,
        type: undefined,
        files: [],
      };
    });
  };

  const modalProps: ModalProps = {
    visible,
    width: 600,
    onCancel,
    afterClose,
  };

  if (type === 'image') {
    const footer = (
      <Footer>
        <div>
          {t('scene.chatWorkspace.messageEditor.upload.label.quantity')}：{fileUrls.length}
        </div>
        <Button onClick={onCancel}>{t('common.action.cancel')}</Button>
        <Button type="primary" onClick={sendImage}>
          {t('common.action.send')}
        </Button>
      </Footer>
    );

    return (
      <Modal
        title={t('scene.chatWorkspace.messageEditor.action.sendImage')}
        footer={footer}
        {...modalProps}
      >
        <Content>
          {fileUrls.map((url) => {
            return <Image src={url} />;
          })}
        </Content>
      </Modal>
    );
  }

  return (
    <Modal
      title={t('scene.chatWorkspace.messageEditor.action.sendVideo')}
      okText={t('common.action.send')}
      okButtonProps={{
        onClick: sendVideo,
      }}
      {...modalProps}
    >
      <Content>
        {fileUrls.map((url) => {
          return (
            <VideoWrapper>
              <VideoPreviewer url={url} />
            </VideoWrapper>
          );
        })}
      </Content>
    </Modal>
  );
}

export default MediaPreview;
